import React, { useState } from 'react';
import ConfirmationModal from '../business/store/ConfirmationModal.jsx';
import useAuth from '../hooks/AuthHook.jsx';
import { postAbnAndAhpra } from '../Api.js';
import { LOCUM_CREDENTIALS, PII_STATUSES } from '../Constants';
import { getMissingCredentials } from '../../helpers/CredentialUtils.js';
import PiiDeclaration, { getPiiDeclarationFromStatus } from './PiiDeclaration.jsx';
import './CredentialsPromptModal.scss';

/**
 * Display details for the locum's current PII status, shared by the sidebar, Manage Account and profile header.
 * @param {Object} profile - needs piiStatus, piiAttestedAt and missingCredentials
 * @returns {{ text: string, missing: boolean, lastConfirmed: string|null }}
 */
export const getPiiStatusDisplay = (profile) => {
  const { piiStatus, piiAttestedAt } = profile;
  return {
    text: (PII_STATUSES[piiStatus] || PII_STATUSES.NOT_PROVIDED).text,
    missing: getMissingCredentials(profile).includes(LOCUM_CREDENTIALS.PII),
    lastConfirmed: piiAttestedAt
      ? new Date(piiAttestedAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
      : null,
  };
};

/**
 * Standalone PII declaration, opened from the sidebar and Manage Account so that confirming PII is its own
 * deliberate action rather than a side effect of saving other details. Every save is recorded as an
 * attestation (even an unchanged "Yes", which refreshes the confirmed date).
 * @param {Function} close
 * @param {string} source - CREDENTIAL_SOURCES value recorded on the audit log
 */
const PiiDeclarationModal = ({ close, source }) => {
  const { profile, setProfile } = useAuth();
  const [piiDeclaration, setPiiDeclaration] = useState(() => getPiiDeclarationFromStatus(profile.piiStatus));
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState('');

  const confirm = () => {
    setLoading(true);
    setErrorText('');
    postAbnAndAhpra(undefined, undefined, piiDeclaration, source)
      .then(({ credentials }) => {
        setProfile({ ...profile, ...credentials });
        setLoading(false);
        close();
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
        setErrorText('Something went wrong saving your answer. Please try again.');
      });
  };

  return (
    <ConfirmationModal
      loading={loading}
      close={close}
      additionalClassName={'credentials-prompt'}
      confirm={confirm}
      confirmText={'Save'}
      disabled={piiDeclaration === null}
    >
      <h3 className={'confirmation-title'}>{'Professional Indemnity Insurance'}</h3>
      <PiiDeclaration value={piiDeclaration} onChange={setPiiDeclaration} name={'pii-declaration-modal'} disabled={loading} />
      {errorText && <p className={'error-text'}>{errorText}</p>}
    </ConfirmationModal>
  );
};

export default PiiDeclarationModal;
